import { ThemeEvents } from '@theme/events';

class FreeShippingProgress extends HTMLElement {
  /** @type {() => void} */
  #onCartUpdate = this.#handleCartUpdate.bind(this);
  /** @type {(event: Event) => void} */
  #onCtaClick = this.#handleCtaClick.bind(this);
  /** @type {(event: Event) => void} */
  #onBundleChange = this.#handleBundleChange.bind(this);

  connectedCallback() {
    this.addEventListener('click', this.#onCtaClick);

    if (this.#isProductPreview()) {
      document.addEventListener('bundle-quantity-change', this.#onBundleChange);
      // SSR may already be correct; re-sync once the picker has selected its default.
      requestAnimationFrame(() => this.#syncFromBundlePicker());
      return;
    }

    // Drawer / cart bars: keep in sync with the real cart total.
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('bundle-quantity-change', this.#onBundleChange);
    this.removeEventListener('click', this.#onCtaClick);
  }

  #isProductPreview() {
    return this.classList.contains('free-shipping-progress--product');
  }

  /**
   * @param {Event} event
   */
  #handleCtaClick(event) {
    const cta = event.target instanceof Element ? event.target.closest('[data-fsp-cta]') : null;
    if (!cta) return;

    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * Product-page bar follows the selected 1/2/3 jar tier — not the cart.
   * @param {Event} event
   */
  #handleBundleChange(event) {
    if (!(event instanceof CustomEvent)) return;
    const priceCents = Number(event.detail?.priceCents);
    if (!Number.isFinite(priceCents) || priceCents < 0) return;
    this.#applyTotal(priceCents);
  }

  #syncFromBundlePicker() {
    const picker =
      this.closest('product-form-component')?.querySelector('product-bundle-picker') ||
      document.querySelector('product-bundle-picker');
    if (!picker) return;

    const selected =
      picker.querySelector('[data-bundle-option].is-selected') ||
      picker.querySelector('[data-bundle-option][aria-checked="true"]');
    if (!(selected instanceof HTMLElement)) return;

    const priceCents = Number(selected.dataset.priceCents || 0);
    if (!Number.isFinite(priceCents) || priceCents <= 0) return;
    this.#applyTotal(priceCents);
  }

  /**
   * Drawer/cart bars are usually re-rendered by section morph — fall back to /cart.js.
   * @param {CustomEvent} event
   */
  #handleCartUpdate(event) {
    if (this.#isProductPreview()) return;

    const sections = event.detail?.data?.sections;
    if (sections) {
      // Another morphing bar may already be correct; if this instance wasn't replaced, refresh from JSON.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#syncFromCartJson());
      });
      return;
    }

    this.#syncFromCartJson();
  }

  async #syncFromCartJson() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      const cart = await response.json();
      this.#applyTotal(Number(cart.total_price) || 0);
    } catch {
      /* ignore */
    }
  }

  /**
   * @param {number} totalCents
   */
  #applyTotal(totalCents) {
    const total = Math.max(0, Math.round(totalCents));
    this.dataset.cartTotal = String(total);

    const threshold = Number(this.dataset.threshold || 0);
    if (!threshold) return;

    const remaining = Math.max(threshold - total, 0);
    const progress = Math.min(Math.round((total * 100) / threshold), 100);
    const qualified = total >= threshold;
    const goalLabel = this.dataset.goalLabel || 'free shipping';

    this.toggleAttribute('data-qualified', qualified);

    const fill = this.querySelector('[data-fsp-fill]');
    if (fill instanceof HTMLElement) fill.style.width = `${progress}%`;

    const track = this.querySelector('.free-shipping-progress__track');
    if (track) track.setAttribute('aria-valuenow', String(total));

    const message = this.querySelector('[data-fsp-message]');
    if (message) {
      message.textContent = qualified
        ? `You've unlocked ${goalLabel}!`
        : `You're ${this.#formatCents(remaining)} away from ${goalLabel}!`;
    }

    const current = this.querySelector('[data-fsp-current]');
    if (current) current.textContent = this.#formatCents(total);

    let cta = this.querySelector('[data-fsp-cta]');
    if (qualified) {
      if (cta instanceof HTMLElement) cta.hidden = true;
      return;
    }

    if (!(cta instanceof HTMLAnchorElement)) {
      const header = this.querySelector('.free-shipping-progress__header');
      if (!header) return;
      cta = document.createElement('a');
      cta.className = 'free-shipping-progress__cta';
      cta.setAttribute('data-fsp-cta', '');
      cta.href = this.dataset.ctaUrl || '#';
      header.appendChild(cta);
    }

    cta.hidden = false;
    cta.textContent = `Add ${this.#formatCents(remaining)} more`;
  }

  /** @param {number} cents */
  #formatCents(cents) {
    const template = this.querySelector('[data-fsp-current]');
    const threshold = this.querySelector('[data-fsp-threshold]');
    const reference = template?.textContent?.trim() || threshold?.textContent?.trim() || '';

    if (reference && reference.match(/\d/)) {
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = String(Math.round(cents / 100));
      const sampleNumber = reference.match(/[\d.,]+/)?.[0] || '';

      if (sampleNumber.includes(',') && sampleNumber.includes('.')) {
        return reference.replace(/[\d.,]+/, amount.replace('.', ','));
      }
      if (sampleNumber.includes(',') && !sampleNumber.includes('.')) {
        return reference.replace(/[\d.,]+/, amount.replace('.', ','));
      }
      if (reference.includes(amountNoDecimals) && !reference.includes('.')) {
        return reference.replace(/[\d.,]+/, amountNoDecimals);
      }
      return reference.replace(/[\d.,]+/, amount);
    }

    return (cents / 100).toFixed(2);
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
